import { ReactNode, useEffect, useState } from "react";

import { Callout, Colors, Icon, InputGroup } from "@blueprintjs/core";
import uniqueId from "lodash/uniqueId";
import Select from "react-select";

import { Box, Flex } from "@/components/layout/flexbox";
import { DateInputWithIcon } from "@/components/pages_logged_in/program/components/set-components/configure-inputs/DateTimeFormInput";
import { useDateTimeConstraints } from "@/components/pages_logged_in/program/components/set-components/configure-inputs/hooks";
import { OptionalFormInput } from "@/components/pages_logged_in/program/components/set-components/OptionalFormInput";
import { StyledFormGroup } from "@/components/pages_logged_in/program/components/set-components/StyledFormGroup";
import { StyledStickyBox } from "@/components/pages_logged_in/program/components/set-components/StyledStickyBox";
import { validateComponentDate } from "@/components/pages_logged_in/program/components/validate-component-date";
import { FlexibleButton } from "@/components/pieces/button/FlexibleButton";
import { Tiptap } from "@/components/pieces/editor/tiptap";
import { StandaloneCheckbox } from "@/components/pieces/form/StandaloneCheckbox";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { Button } from "@/components/ui/button";
import { BLUE_TEXT_COLOR, CARD_CLASSES } from "@/css/constants";
import { ChildProgramComponentInput, ComponentTypeEnum } from "@/graphql";
import { cn } from "@/lib/utils";
import { ComponentTabProps, MainComponent } from "@/types/programs";
import { ensureNonNullArray } from "@/utils/array";
import { getBrowserTimezone } from "@/utils/datetime";

import { sortComponentsByDate } from "./sort-components-by-date";

const sessionNumberOptions = [
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
];

enum MeetingViewMode {
  Read = "Read",
  Edit = "Edit",
}

const renderError = (
  errorType: string | null,
  targetType: string,
  message: string | null,
  valid: boolean,
): ReactNode | null => {
  if (!valid && errorType === targetType) {
    return (
      <Box color={"#c23030"} fontSize={14} mt={10}>
        {message}
      </Box>
    );
  }

  return null;
};

interface SessionCardProps {
  session: ChildProgramComponentInput;
  onEdit?: (session: ChildProgramComponentInput) => void;
  onDelete: (session: ChildProgramComponentInput) => void;
  allowDelete: boolean;
  error: string;
  timezone: string | null;
  setTimeZone: (timezone: string) => void;
  programComponent?: MainComponent;
  onChange: (session: ChildProgramComponentInput) => void;
  errorEndDate?: ReactNode | null;
  errorLaunchDate?: ReactNode | null;
}

function SessionCard({
  session,
  onDelete,
  allowDelete = true,
  error = "",
  timezone,
  setTimeZone,
  programComponent,
  onChange = () => {},
  errorEndDate,
  errorLaunchDate,
}: SessionCardProps) {
  const [visibleGuidance, setVisibleGuidance] = useState(false);
  const [meetingViewMode, setMeetingViewMode] = useState(MeetingViewMode.Read);

  useEffect(() => {
    setVisibleGuidance(!!session?.participantGuidance);
  }, [session]);

  useEffect(() => {
    if (visibleGuidance !== null && !visibleGuidance) {
      onChange({ ...session, participantGuidance: null });
    }
  }, [visibleGuidance]);

  const hasChangeStartDate = false;
  const hasChangeDueDate = false;
  const hasChangeEndDate = false;

  const products = ensureNonNullArray(programComponent?.products);

  const { minLaunchDate, minEndDate, maxLaunchDate, maxEndDate } =
    useDateTimeConstraints({
      launchDate: session?.targetStartDate,
      dueDate: session?.targetEndDate,
      endDate: session?.targetEndDate,
      hasChangeStartDate,
      hasChangeDueDate,
      hasChangeEndDate,
    });

  return (
    <div className={cn(CARD_CLASSES, "flex flex-col flex-1 p-6 gap-6")}>
      <div className="flex items-center font-semibold font-lg">
        {meetingViewMode === MeetingViewMode.Read ? (
          <>
            <Box>{session?.name}</Box>
            <Icon
              icon="edit"
              color={BLUE_TEXT_COLOR}
              style={{ marginLeft: 10 }}
              onClick={() => {
                setMeetingViewMode(MeetingViewMode.Edit);
              }}
            />
          </>
        ) : (
          <>
            <InputGroup
              onChange={(e) => {
                onChange({ ...session, name: e.currentTarget.value });
              }}
              value={session?.name ?? ""}
              large={true}
              fill={true}
              placeholder={"Session name"}
              autoFocus={true}
              disabled={false}
            />
          </>
        )}

        <Box flex={1} />
        {allowDelete && (
          <Button
            className="ml-2.5 !p-0 !contents"
            intent="danger"
            variant="minimal"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(session);
            }}
          >
            <Icon icon="trash" className="text-red-700" />
          </Button>
        )}
      </div>

      <StyledFormGroup label="Type">
        <Select
          value={{
            value: session?.productId,
            label:
              products.find((item) => item.id === session?.productId)?.name ||
              "",
          }}
          onChange={(selected) => {
            onChange({ ...session, productId: selected?.value });
          }}
          options={products.map((item) => ({
            value: item.id,
            label: item.name,
          }))}
          isSearchable={false}
          styles={{
            control: (styles) => ({
              ...styles,
            }),
            menu: (styles) => ({
              ...styles,
              zIndex: 999,
            }),
          }}
        />
      </StyledFormGroup>

      <StyledFormGroup
        label="Session Window Start"
        subLabel="Earliest date a session can be scheduled for."
      >
        <DateInputWithIcon
          value={session?.targetStartDate}
          onChange={(value) => {
            onChange({ ...session, targetStartDate: value });
          }}
          onChangeTimezone={setTimeZone}
          timezone={timezone}
          min={minLaunchDate}
          max={maxLaunchDate}
        />
        {errorLaunchDate}
      </StyledFormGroup>

      <StyledFormGroup
        label="Session Window End"
        subLabel="Latest date a session can be scheduled for."
      >
        <DateInputWithIcon
          value={session?.targetEndDate}
          onChange={(value) => {
            onChange({ ...session, targetEndDate: value });
          }}
          onChangeTimezone={setTimeZone}
          timezone={timezone}
          min={minEndDate}
          max={maxEndDate}
        />
        {errorEndDate}
      </StyledFormGroup>

      <Flex alignItems={"center"}>
        <StandaloneCheckbox
          onChange={() => setVisibleGuidance(!visibleGuidance)}
          checked={visibleGuidance ?? false}
        />
        <Clickable onClick={() => setVisibleGuidance(!visibleGuidance)}>
          <Box style={{ fontSize: 16 }} ml={2}>
            Include guidance for participants
          </Box>
        </Clickable>
      </Flex>
      {visibleGuidance && (
        <Box style={{ backgroundColor: Colors.WHITE }} mt={2}>
          <Tiptap
            content={session?.participantGuidance || ""}
            onChange={(value) => {
              onChange({ ...session, participantGuidance: value });
            }}
            placeholder="eg. Find a coach"
            autoFocus={true}
          />
        </Box>
      )}

      <OptionalFormInput
        optional={session?.optional ?? programComponent?.optional ?? false}
        onChange={(value) => onChange({ ...session, optional: value })}
      />

      {error && (
        <Box mt={2}>
          <Callout intent="danger" icon="warning-sign">
            {error}
          </Callout>
        </Box>
      )}
    </div>
  );
}

export function Journey({
  onNext,
  introSessionInput,
  meetingSessionInput,
  programComponent,
}: ComponentTabProps) {
  const [timezone, setTimezone] = useState(getBrowserTimezone());

  const [visibleGuidance, setVisibleGuidance] = useState(false);
  const launchDate = introSessionInput?.targetStartDate;
  const endDate = introSessionInput?.targetEndDate;
  const guidance = introSessionInput?.participantGuidance;
  const introSessionCount =
    introSessionInput?.introSessionCount || sessionNumberOptions[0].value;
  const [sessions, setSessions] = useState<ChildProgramComponentInput[]>([]);
  const [inputErrors, setInputErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (meetingSessionInput?.length === 0) {
      setSessions([
        {
          id: `new-session-${uniqueId()}`,
          name: `Session #${meetingSessionInput?.length + 1}`,
        },
      ]);
    }
  }, [meetingSessionInput]);

  useEffect(() => {
    setVisibleGuidance(!!introSessionInput?.participantGuidance);
  }, [introSessionInput]);

  useEffect(() => {
    if (visibleGuidance !== null && !visibleGuidance) {
      onIntroSessionChange({
        participantGuidance: undefined,
      });
    }
  }, [visibleGuidance]);

  useEffect(() => {
    if (meetingSessionInput?.length > 0) {
      const sortedSessions = sortComponentsByDate(meetingSessionInput);
      setSessions(sortedSessions);
    }
  }, [meetingSessionInput]);

  const onIntroSessionChange = (value: Partial<ChildProgramComponentInput>) => {
    onNext({
      introSessionValue: {
        ...introSessionInput,
        ...value,
        type: ComponentTypeEnum.OneOneCoachingIntro,
      },
      isNext: false,
      isSave: false,
    });
  };

  const { minLaunchDate, minEndDate, maxLaunchDate, maxEndDate } =
    useDateTimeConstraints({
      launchDate,
      dueDate: null,
      endDate,
      hasChangeStartDate: false,
      hasChangeDueDate: false,
      hasChangeEndDate: false,
    });

  const onPressNext = (
    newSession: ChildProgramComponentInput[],
    isSave = true,
    isNext = true,
  ) => {
    if (isSave) {
      const errors: Record<string, string> = {};
      newSession.forEach((q) => {
        if (!q.name || (q.name && !q.name.trim())) {
          errors[q.id ?? ""] = "Please provide a name";
        }
      });
      setInputErrors(errors);
      if (Object.keys(errors).length > 0) {
        return;
      }
    }
    setSessions(newSession);
    onNext({
      isSave,
      isNext,
      introSessionValue: {
        id: introSessionInput?.id,
        targetStartDate: launchDate,
        targetEndDate: endDate,
        participantGuidance: guidance,
        introSessionCount,
        type: ComponentTypeEnum.OneOneCoachingIntro,
      },
      meetingSessionValue: newSession.map((session) => ({
        ...session,
      })),
    });
  };
  const completedSetUpIntro = launchDate && endDate;
  const completedSetUpSessions = sessions.every(
    (session) =>
      session.name &&
      session.targetStartDate &&
      session.targetEndDate &&
      session.productId,
  );

  const validationData = validateComponentDate(
    {
      targetStartDate: introSessionInput?.targetStartDate,
      targetEndDate: introSessionInput?.targetEndDate,
      targetDueDate: introSessionInput?.targetEndDate,
      type: ComponentTypeEnum.OneOneCoachingSession,
    },
    introSessionInput,
  );
  const { errorType, valid, message } = validationData;
  const hasDateData =
    introSessionInput?.targetStartDate && introSessionInput?.targetEndDate;
  const errorEndDate =
    hasDateData && renderError(errorType, "targetEndDate", message, valid);
  const errorLaunchDate =
    hasDateData && renderError(errorType, "targetStartDate", message, valid);

  const hasInvalidSession = sessions.some((session) => {
    const validationData = validateComponentDate(
      {
        targetStartDate: session?.targetStartDate,
        targetEndDate: session?.targetEndDate,
        targetDueDate: session?.targetEndDate,
        type: ComponentTypeEnum.OneOneCoachingSession,
      },
      session,
    );
    const { valid } = validationData;
    return !valid;
  });

  const disabledSubmit =
    !completedSetUpIntro ||
    !completedSetUpSessions ||
    !valid ||
    hasInvalidSession;

  return (
    <>
      <div className="flex flex-col gap-6 max-w-148 my-0 mx-auto p-9">
        <div>
          <div className="text-2xl font-semibold">Design coaching journey</div>
          <div className="text-subtext mt-2.5">
            Outline the coaching experience from start to finish
          </div>
        </div>

        <div
          className={cn(
            CARD_CLASSES,
            "flex-1 p-6 bg-blue-100 flex flex-col gap-6",
          )}
        >
          <div>
            <div className="text-lg font-bold">Finding a Coach</div>
            <div className="text-subtext text-sm">
              Participants start with intro sessions to choose their preferred
              coach. After completing an intro session, they can schedule
              regular coaching sessions.
            </div>
          </div>

          <StyledFormGroup
            label="Max Intro Sessions "
            subLabel="Participants can schedule up to this number"
          >
            <Flex
              css={{ position: "relative", zIndex: 999, alignItems: "center" }}
            >
              <Select
                value={sessionNumberOptions.find(
                  (o) => o.value === introSessionCount,
                )}
                onChange={(selected) => {
                  if (!selected) return;
                  onIntroSessionChange({
                    introSessionCount: selected.value,
                  });
                }}
                options={sessionNumberOptions}
                isSearchable={false}
                styles={{
                  control: (styles) => ({
                    ...styles,
                    minWidth: 80,
                  }),
                }}
              />
            </Flex>
          </StyledFormGroup>

          <StyledFormGroup
            label="Session Window Start"
            subLabel="Earliest date a session can be scheduled for."
          >
            <DateInputWithIcon
              value={launchDate}
              onChange={(value) => {
                onIntroSessionChange({
                  targetStartDate: value,
                });
              }}
              onChangeTimezone={setTimezone}
              timezone={timezone}
              min={minLaunchDate}
              max={maxLaunchDate}
            />
            {errorLaunchDate}
          </StyledFormGroup>

          <StyledFormGroup
            label="Session Window End"
            subLabel="Latest date a session can be scheduled for."
          >
            <DateInputWithIcon
              value={endDate}
              onChange={(value) => {
                onIntroSessionChange({
                  targetEndDate: value,
                });
              }}
              onChangeTimezone={setTimezone}
              timezone={timezone}
              min={minEndDate}
              max={maxEndDate}
            />
            {errorEndDate}
          </StyledFormGroup>

          <div className="flex flex-col gap-3">
            <Flex alignItems={"center"}>
              <StandaloneCheckbox
                onChange={() => setVisibleGuidance(!visibleGuidance)}
                checked={visibleGuidance ?? false}
              />
              <Clickable onClick={() => setVisibleGuidance(!visibleGuidance)}>
                <Box style={{ fontSize: 16 }} ml={2}>
                  Include guidance for participants
                </Box>
              </Clickable>
            </Flex>

            {visibleGuidance && (
              <Box style={{ backgroundColor: Colors.WHITE }}>
                <Tiptap
                  content={guidance || ""}
                  onChange={(value) => {
                    onIntroSessionChange({
                      participantGuidance: value,
                    });
                  }}
                  placeholder="eg. Find a coach"
                  autoFocus={true}
                />
              </Box>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="text-lg font-semibold">Coaching Sessions</div>

          {sessions.map((ses, index) => {
            const isSingleLastItem =
              index === sessions.length - 1 && sessions.length === 1;
            const hasDateData = ses?.targetStartDate && ses?.targetEndDate;
            const validationData = validateComponentDate(
              {
                targetStartDate: ses?.targetStartDate,
                targetEndDate: ses?.targetEndDate,
                targetDueDate: ses?.targetEndDate,
                type: ComponentTypeEnum.OneOneCoachingSession,
              },
              ses,
            );
            const { errorType, valid, message } = validationData;

            const errorEndDate =
              hasDateData &&
              renderError(errorType, "targetEndDate", message, valid);
            const errorLaunchDate =
              hasDateData &&
              renderError(errorType, "targetStartDate", message, valid);

            return (
              <SessionCard
                key={ses.id}
                session={ses}
                onDelete={(ses) => {
                  onPressNext(
                    sessions.filter((q) => q.id !== ses.id),
                    false,
                    false,
                  );
                }}
                error={inputErrors[ses?.id ?? ""]}
                allowDelete={!isSingleLastItem}
                timezone={timezone}
                setTimeZone={setTimezone}
                programComponent={programComponent}
                onChange={(session) => {
                  setSessions(
                    sessions.map((q) => {
                      if (q.id === session.id) {
                        return {
                          ...session,
                          type: ComponentTypeEnum.OneOneCoachingSession,
                        };
                      }
                      return q;
                    }),
                  );
                }}
                errorEndDate={errorEndDate}
                errorLaunchDate={errorLaunchDate}
              />
            );
          })}
        </div>

        <Box mt={4}>
          <FlexibleButton
            className="!text-sm"
            rightIcon="plus"
            onClick={() => {
              onPressNext(
                [
                  ...sessions,
                  {
                    id: `new-session-${uniqueId()}`,
                    name: `Session #${sessions.length + 1}`,
                    optional: programComponent?.optional ?? false,
                  },
                ],
                false,
                false,
              );
            }}
          >
            Add Session
          </FlexibleButton>
        </Box>
      </div>

      <StyledStickyBox>
        <div className="flex flex-col gap-6 max-w-148 my-0 mx-auto p-9">
          <div>
            <Button
              className="min-w-40 px-4 py-2.5"
              intent="primary"
              disabled={disabledSubmit}
              onClick={() => {
                onPressNext(sessions, true, true);
              }}
            >
              Continue
            </Button>
          </div>
        </div>
      </StyledStickyBox>
    </>
  );
}
